<template>
  <div class="cloture-shell">
    <NavBar />

    <main class="page">
      <header class="page-header">
        <div>
          <p class="eyebrow">{{ villeActive || 'Marché du jour' }}</p>
          <h1>Clôture</h1>
        </div>
        <span class="status-pill" :class="{ closed: bord.statut === 'cloture' }">
          {{ bord.statut === 'cloture' ? 'Journée clôturée' : 'Journée ouverte' }}
        </span>
      </header>

      <section class="panel no-active-day" v-if="noJourneeActive">
        <h2>Aucune journée active</h2>
        <p>Sélectionnez ou ouvrez un marché actif depuis le tableau de bord avant de lancer la clôture.</p>
      </section>

      <section class="metrics-grid" aria-label="Résumé financier" v-else>
        <article class="metric-card">
          <span>CA fidélisé</span>
          <strong>{{ formatEuro(bord.ca_fidelise) }}</strong>
          <small>{{ bord.nb_passages_carte || 0 }} passages carte</small>
        </article>
        <article class="metric-card">
          <span>CA sans fidélité</span>
          <strong>{{ formatEuro(bord.ca_sans_fidelite || 0) }}</strong>
          <small>Ventes sans carte</small>
        </article>
        <article class="metric-card accent">
          <span>CA total</span>
          <strong>{{ formatEuro(totalTheorique) }}</strong>
          <small>Fidélisé + sans carte</small>
        </article>
        <article class="metric-card">
          <span>Dépenses hors fond</span>
          <strong>{{ formatEuro(totalDepensesOperationnelles) }}</strong>
          <small>{{ depensesOperationnelles.length }} ligne(s)</small>
        </article>
        <article class="metric-card fund">
          <span>Fond de caisse</span>
          <strong>{{ formatEuro(totalFondCaisse) }}</strong>
          <small>{{ fondCaisseDepense ? 'Enregistré' : 'Non renseigné' }}</small>
        </article>
        <article class="metric-card success">
          <span>Bénéfice</span>
          <strong>{{ formatEuro(beneficeCalcule) }}</strong>
          <small>CA total - dépenses hors fond</small>
        </article>
        <article class="metric-card">
          <span>CB</span>
          <strong>{{ formatEuro(bord.ca_cb || 0) }}</strong>
          <small>Paiements carte</small>
        </article>
        <article class="metric-card">
          <span>Espèces</span>
          <strong>{{ formatEuro(bord.ca_especes || 0) }}</strong>
          <small>Paiements cash</small>
        </article>
      </section>

      <div class="content-grid" v-if="!noJourneeActive">
        <section class="panel">
          <div class="panel-header">
            <div>
              <p class="section-kicker">Dépenses opérationnelles</p>
              <h2>Dépenses du jour</h2>
            </div>
            <strong>{{ formatEuro(totalDepensesOperationnelles) }}</strong>
          </div>

          <div class="expense-form">
            <div class="form-row">
              <label>
                <span>Catégorie</span>
                <select v-model="nouvDepense.categorie" class="input">
                  <option value="Stock">Stock / marchandise</option>
                  <option value="Place">Droit de place</option>
                  <option value="Carburant">Carburant</option>
                  <option value="Salaire">Salaire</option>
                  <option value="Autre">Autre</option>
                </select>
              </label>
              <label>
                <span>Montant</span>
                <input v-model.number="nouvDepense.montant" class="input" type="number" min="0" step="0.01" placeholder="0,00" />
              </label>
            </div>
            <label>
              <span>Libellé</span>
              <input v-model="nouvDepense.libelle" class="input" placeholder="Ex: Droit de place" />
            </label>
            <button class="btn btn-secondary" @click="ajouterDepense" :disabled="!nouvDepense.montant || nouvDepense.montant <= 0">
              Ajouter la dépense
            </button>
          </div>

          <div class="expense-list" v-if="depenses.length">
            <article class="expense-item" v-for="d in depenses" :key="d.id">
              <div>
                <span class="badge" :class="badgeCategorie(d.categorie)">{{ labelCategorie(d.categorie) }}</span>
                <p>{{ d.libelle || 'Dépense' }}</p>
              </div>
              <div class="expense-actions">
                <strong>{{ formatEuro(d.montant) }}</strong>
                <button class="icon-button danger" @click="supprimerDepense(d.id)" aria-label="Supprimer la dépense">×</button>
              </div>
            </article>
          </div>
          <p class="empty-state" v-else>Aucune dépense enregistrée.</p>
        </section>

        <aside class="side-stack">
          <section class="panel fund-panel">
            <div class="panel-header">
              <div>
                <p class="section-kicker">Fond de caisse</p>
                <h2>Caisse de départ</h2>
              </div>
              <span class="save-state" :class="{ saved: fondCaisseDepense }">
                {{ fondCaisseDepense ? 'Sauvegardé' : 'À saisir' }}
              </span>
            </div>
            <label>
              <span>Montant du fond</span>
              <input v-model.number="fondCaisse" class="input fund-input" type="number" min="0" step="0.01" placeholder="0,00" />
            </label>
            <button class="btn btn-primary" @click="enregistrerFondCaisse" :disabled="!fondCaisse || fondCaisse <= 0 || loadingFond">
              {{ loadingFond ? 'Sauvegarde...' : fondCaisseDepense ? 'Mettre à jour' : 'Enregistrer le fond' }}
            </button>
            <div class="fund-record" v-if="fondCaisseDepense">
              <span>Catégorie</span>
              <strong>Fond_de_caisse</strong>
              <span>Libellé</span>
              <strong>{{ fondCaisseDepense.libelle || 'Fond de caisse' }}</strong>
            </div>
            <p class="inline-success" v-if="fondMessage">{{ fondMessage }}</p>
          </section>

          <section class="panel" v-if="bord.statut !== 'cloture'">
            <div class="panel-header">
              <div>
                <p class="section-kicker">Contexte</p>
                <h2>Conditions du jour</h2>
              </div>
            </div>
            <div class="chips-block">
              <span>Météo</span>
              <div class="chips">
                <button v-for="m in meteos" :key="m.val" class="chip" :class="{ active: contexte.meteo === m.val }" @click="contexte.meteo = m.val">{{ m.label }}</button>
              </div>
            </div>
            <div class="chips-block">
              <span>Contexte</span>
              <div class="chips">
                <button v-for="c in contextes" :key="c.val" class="chip" :class="{ active: contexte.contexte === c.val }" @click="contexte.contexte = c.val">{{ c.label }}</button>
              </div>
            </div>
            <label>
              <span>Commentaire</span>
              <input v-model="contexte.commentaire" class="input" placeholder="Ex: Forte affluence" />
            </label>
            <button class="btn btn-secondary" @click="sauvegarderContexte">Sauvegarder</button>
            <p class="inline-success" v-if="contexteOk">Contexte sauvegardé</p>
          </section>
        </aside>
      </div>

      <section class="panel closure-panel" v-if="!noJourneeActive && bord.statut !== 'cloture'">
        <div class="panel-header">
          <div>
            <p class="section-kicker">Validation finale</p>
            <h2>Encaissement réel</h2>
          </div>
          <strong>{{ formatEuro(totalTheorique) }}</strong>
        </div>
        <div class="closure-grid">
          <label>
            <span>CA réel encaissé</span>
            <input v-model.number="caReel" class="input ca-input" type="number" min="0" step="0.01" placeholder="0,00" />
          </label>
          <div class="variance-box" v-if="caReel > 0">
            <span>Écart théorique</span>
            <strong :class="ecart >= 0 ? 'positive' : 'negative'">{{ ecart >= 0 ? '+' : '' }}{{ formatEuro(ecart) }}</strong>
          </div>
        </div>
        <button class="btn btn-primary wide" @click="demanderConfirmationCloture" :disabled="!caReel || caReel <= 0 || loadingCloture">
          {{ loadingCloture ? 'Clôture en cours...' : 'Clôturer la journée' }}
        </button>
      </section>

      <section class="panel result-panel" v-if="resultat">
        <div class="panel-header">
          <div>
            <p class="section-kicker">Clôture enregistrée</p>
            <h2>Résultat journée</h2>
          </div>
        </div>
        <div class="result-grid">
          <div><span>CA réel</span><strong>{{ formatEuro(caReel) }}</strong></div>
          <div><span>Dépenses hors fond</span><strong>{{ formatEuro(resultat.total_depenses) }}</strong></div>
          <div><span>Bénéfice</span><strong>{{ formatEuro(resultat.marge_brute) }}</strong></div>
          <div><span>Marge</span><strong>{{ resultat.marge_pct || 0 }} %</strong></div>
        </div>
      </section>

      <p class="message success-message" v-if="successMessage">{{ successMessage }}</p>
      <p class="message error-message" v-if="errorMessage">{{ errorMessage }}</p>
    </main>

    <div class="modal-overlay" v-if="popupCloture" @click.self="popupCloture = false">
      <div class="modal">
        <span class="modal-mark">Validation</span>
        <h3>Clôturer la journée ?</h3>
        <p>CA réel encaissé : <strong>{{ formatEuro(caReel) }}</strong></p>
        <p>Total théorique : <strong>{{ formatEuro(totalTheorique) }}</strong></p>
        <p>Dépenses hors fond : <strong>{{ formatEuro(totalDepensesOperationnelles) }}</strong></p>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="popupCloture = false">Annuler</button>
          <button class="btn btn-primary" @click="confirmerCloture" :disabled="loadingCloture">
            {{ loadingCloture ? 'En cours...' : 'Confirmer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api    from '../api'

export default {
  name: 'ClotureView',
  components: { NavBar },
  data() {
    return {
      villeActive:  localStorage.getItem('marche_ville_active') || '',
      bord:         { ca_fidelise: 0, ca_sans_fidelite: 0, ca_especes: 0, ca_cb: 0, nb_passages_carte: 0, total_depenses: 0, ca_total: null, statut: 'en_cours' },
      depenses:     [],
      nouvDepense:  { categorie: 'Stock', libelle: '', montant: null },
      contexte:     { meteo: '', contexte: '', commentaire: '' },
      contexteOk:   false,
      caReel:       null,
      fondCaisse:   null,
      fondMessage:  '',
      successMessage: '',
      errorMessage: '',
      resultat:     null,
      noJourneeActive: false,
      loadingCloture: false,
      loadingFond: false,
      popupCloture: false,
      meteos: [
        { val: 'soleil',  label: 'Soleil' },
        { val: 'nuageux', label: 'Nuageux' },
        { val: 'pluie',   label: 'Pluie' },
        { val: 'vent',    label: 'Vent' },
        { val: 'froid',   label: 'Froid' }
      ],
      contextes: [
        { val: 'normal',    label: 'Normal' },
        { val: 'vacances',  label: 'Vacances' },
        { val: 'ferie',     label: 'Férié' },
        { val: 'evenement', label: 'Événement' }
      ]
    }
  },
  computed: {
    depensesOperationnelles() {
      return this.depenses.filter(d => d.categorie !== 'Fond_de_caisse')
    },
    fondCaisseDepense() {
      return this.depenses.find(d => d.categorie === 'Fond_de_caisse') || null
    },
    totalDepensesOperationnelles() {
      return this.depensesOperationnelles.reduce((s, d) => s + parseFloat(d.montant || 0), 0)
    },
    totalFondCaisse() {
      return this.fondCaisseDepense ? parseFloat(this.fondCaisseDepense.montant || 0) : 0
    },
    totalTheorique() {
      return parseFloat(this.bord.ca_fidelise || 0) + parseFloat(this.bord.ca_sans_fidelite || 0)
    },
    beneficeCalcule() {
      return this.totalTheorique - this.totalDepensesOperationnelles
    },
    ecart() {
      return parseFloat(this.caReel || 0) - this.totalTheorique
    }
  },
  methods: {
    formatEuro(v) {
      return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    setMessage(type, message) {
      this.successMessage = type === 'success' ? message : ''
      this.errorMessage = type === 'error' ? message : ''
      if (message) setTimeout(() => {
        this.successMessage = ''
        this.errorMessage = ''
      }, 3500)
    },
    badgeCategorie(c) {
      const map = { Stock: 'badge-blue', Place: 'badge-orange', Carburant: 'badge-green', Salaire: 'badge-navy', Autre: 'badge-gray' }
      return map[c] || 'badge-gray'
    },
    labelCategorie(c) {
      const map = { Fond_de_caisse: 'Fond de caisse', Stock: 'Stock', Place: 'Place', Carburant: 'Carburant', Salaire: 'Salaire', Autre: 'Autre' }
      return map[c] || c
    },
    normaliserBord(data) {
      const caSansFidelite = data?.ca_sans_fidelite ?? data?.ca_sans_carte ?? 0
      const caCb = data?.ca_cb ?? data?.cb ?? 0
      const caEspeces = data?.ca_especes ?? data?.especes ?? 0
      return {
        ca_fidelise: 0,
        ca_sans_fidelite: 0,
        ca_especes: 0,
        ca_cb: 0,
        nb_passages_carte: 0,
        total_depenses: 0,
        ca_total: null,
        statut: 'en_cours',
        ...data,
        ca_sans_fidelite: caSansFidelite,
        ca_especes: caEspeces,
        ca_cb: caCb
      }
    },
    async charger() {
      const ville = this.villeActive
      try {
        this.noJourneeActive = false
        if (!ville) {
          const res = await api.get('/journee/today')
          const journees = Array.isArray(res.data) ? res.data : []
          if (journees.length === 0) {
            this.bord = this.normaliserBord()
            this.depenses = []
            this.noJourneeActive = true
            this.setMessage('error', 'Aucune journée active trouvée.')
            return
          }
          if (journees.length === 1 && journees[0].ville) {
            this.villeActive = journees[0].ville
            localStorage.setItem('marche_ville_active', this.villeActive)
            return this.charger()
          }
          if (journees.length > 1) {
            this.bord = this.normaliserBord()
            this.depenses = []
            this.noJourneeActive = true
            this.setMessage('error', 'Sélectionnez un marché actif depuis le tableau de bord avant la clôture.')
            return
          }
        }

        const queryVille = this.villeActive
          ? '?ville=' + encodeURIComponent(this.villeActive)
          : ''
        const [b, d] = await Promise.all([
          this.villeActive
            ? api.get('/journee/today/' + encodeURIComponent(this.villeActive))
            : api.get('/journee/today'),
          api.get('/depenses/today' + queryVille)
        ])
        const bordData = Array.isArray(b.data) ? (b.data[0] || {}) : b.data
        this.bord     = this.normaliserBord(bordData)
        this.depenses = Array.isArray(d.data) ? d.data : []
        this.noJourneeActive = Boolean(bordData.no_journee_active)
        if (this.noJourneeActive) {
          this.setMessage('error', 'Aucune journée active trouvée pour ce marché.')
        }
        this.contexte.meteo       = bordData.meteo || ''
        this.contexte.contexte    = bordData.contexte || ''
        this.contexte.commentaire = bordData.commentaire || ''
        const fondDep = this.depenses.find(dep => dep.categorie === 'Fond_de_caisse')
        this.fondCaisse = fondDep ? parseFloat(fondDep.montant || 0) : null
      } catch(err) {
        console.error(err)
        this.setMessage('error', 'Impossible de charger la clôture.')
      }
    },
    async ajouterDepense() {
      try {
        await api.post('/depenses', { ...this.nouvDepense, ville: this.villeActive || null })
        this.nouvDepense = { categorie: 'Stock', libelle: '', montant: null }
        await this.charger()
        this.setMessage('success', 'Dépense ajoutée.')
      } catch(err) {
        console.error(err)
        this.setMessage('error', 'Impossible d’ajouter la dépense.')
      }
    },
    async supprimerDepense(id) {
      try {
        await api.delete(`/depenses/${id}`)
        await this.charger()
        this.setMessage('success', 'Dépense supprimée.')
      } catch(err) {
        console.error(err)
        this.setMessage('error', 'Impossible de supprimer la dépense.')
      }
    },
    async sauvegarderContexte() {
      try {
        await api.patch('/journee/contexte', { ...this.contexte, ville: this.villeActive || null })
        this.contexteOk = true
        setTimeout(() => this.contexteOk = false, 2000)
      } catch(err) {
        console.error(err)
        this.setMessage('error', 'Impossible de sauvegarder le contexte.')
      }
    },
    async enregistrerFondCaisse() {
      this.loadingFond = true
      this.fondMessage = ''
      try {
        await api.post('/depenses', {
          categorie: 'Fond_de_caisse',
          libelle:   'Fond de caisse',
          montant:   this.fondCaisse,
          ville:     this.villeActive || null
        })
        await this.charger()
        this.fondMessage = 'Fond de caisse sauvegardé.'
      } catch(err) {
        console.error(err)
        this.setMessage('error', 'Impossible de sauvegarder le fond de caisse.')
      } finally {
        this.loadingFond = false
      }
    },
    demanderConfirmationCloture() { this.popupCloture = true },
    async confirmerCloture() {
      this.loadingCloture = true
      try {
        const res = await api.post('/journee/cloture', {
          ca_total_saisi: this.caReel,
          ville: this.villeActive || null
        })
        this.resultat     = res.data
        this.bord.statut  = 'cloture'
        this.popupCloture = false
        this.setMessage('success', 'Journée clôturée.')
      } catch(err) {
        console.error(err)
        this.setMessage('error', 'Impossible de clôturer la journée.')
      } finally {
        this.loadingCloture = false
      }
    }
  },
  mounted() { this.charger() }
}
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
}

.cloture-shell {
  min-height: 100vh;
  background: #F8FAFC;
  color: #0F172A;
}

.page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 32px 24px 56px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
}

.eyebrow,
.section-kicker {
  margin: 0 0 6px;
  color: #1E6FDC;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1,
h2,
h3,
p {
  margin-top: 0;
}

h1 {
  margin-bottom: 0;
  color: #0D1B3D;
  font-size: 34px;
  line-height: 1.08;
}

h2 {
  margin-bottom: 0;
  color: #0D1B3D;
  font-size: 18px;
}

.status-pill,
.save-state {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 14px;
  border: 1px solid #E2E8F0;
  border-radius: 999px;
  background: #FFFFFF;
  color: #64748B;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.status-pill.closed,
.save-state.saved {
  border-color: rgba(34, 197, 94, 0.28);
  background: rgba(34, 197, 94, 0.1);
  color: #15803D;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.metric-card,
.panel {
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  background: #FFFFFF;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
  max-width: 100%;
}

.metric-card {
  padding: 18px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  border-color: rgba(30, 111, 220, 0.26);
  box-shadow: 0 22px 48px rgba(15, 23, 42, 0.09);
}

.metric-card span,
.metric-card small,
label span,
.chips-block > span,
.fund-record span,
.result-grid span,
.variance-box span {
  color: #64748B;
  font-size: 12px;
  font-weight: 700;
}

.metric-card strong {
  display: block;
  margin: 9px 0 4px;
  color: #0F172A;
  font-size: 24px;
  line-height: 1.15;
}

.metric-card.accent {
  border-color: rgba(30, 111, 220, 0.28);
  background: linear-gradient(180deg, #FFFFFF 0%, #EFF6FF 100%);
}

.metric-card.fund {
  background: linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%);
}

.metric-card.success {
  border-color: rgba(34, 197, 94, 0.28);
  background: linear-gradient(180deg, #FFFFFF 0%, #F0FDF4 100%);
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.85fr);
  gap: 18px;
  align-items: start;
}

.side-stack {
  display: grid;
  gap: 18px;
}

.panel {
  padding: 22px;
}

.no-active-day {
  margin-bottom: 18px;
}

.no-active-day p {
  margin: 8px 0 0;
  color: #64748B;
  font-size: 14px;
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.panel-header > strong {
  color: #0D1B3D;
  font-size: 20px;
}

.expense-form,
.chips-block {
  display: grid;
  gap: 12px;
}

.form-row,
.closure-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

label {
  display: grid;
  gap: 7px;
}

.input {
  width: 100%;
  min-height: 46px;
  box-sizing: border-box;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  background: #FFFFFF;
  color: #0F172A;
  font-size: 15px;
  outline: none;
  padding: 0 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input:focus {
  border-color: #1E6FDC;
  box-shadow: 0 0 0 4px rgba(30, 111, 220, 0.12);
}

.fund-input,
.ca-input {
  min-height: 58px;
  font-size: 28px;
  font-weight: 800;
}

.btn {
  min-height: 46px;
  border: 0;
  border-radius: 14px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 800;
  padding: 0 18px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, opacity 0.2s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.btn-primary {
  background: #1E6FDC;
  color: #FFFFFF;
  box-shadow: 0 12px 24px rgba(30, 111, 220, 0.22);
}

.btn-secondary {
  background: #EFF6FF;
  color: #0D1B3D;
}

.wide {
  width: 100%;
  margin-top: 16px;
}

.expense-list {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.expense-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 13px 14px;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.expense-item:hover {
  border-color: rgba(30, 111, 220, 0.28);
  background: #F8FAFC;
}

.expense-item p {
  margin: 8px 0 0;
  color: #0F172A;
  font-size: 14px;
  font-weight: 700;
}

.expense-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.icon-button {
  width: 34px;
  height: 34px;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  background: #FFFFFF;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.icon-button.danger {
  color: #DC2626;
}

.icon-button:hover {
  border-color: rgba(220, 38, 38, 0.24);
  background: #FEF2F2;
}

.badge {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.badge-blue { background: #DBEAFE; color: #1D4ED8; }
.badge-orange { background: #FFEDD5; color: #C2410C; }
.badge-green { background: #DCFCE7; color: #15803D; }
.badge-navy { background: #E0E7FF; color: #0D1B3D; }
.badge-gray { background: #F1F5F9; color: #475569; }

.empty-state {
  margin: 18px 0 0;
  border: 1px dashed #CBD5E1;
  border-radius: 16px;
  color: #64748B;
  padding: 18px;
  text-align: center;
}

.fund-panel {
  border-color: rgba(30, 111, 220, 0.24);
}

.fund-record {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 12px;
  margin-top: 14px;
  border-radius: 16px;
  background: #F8FAFC;
  padding: 14px;
}

.fund-record strong {
  color: #0D1B3D;
  font-size: 13px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  min-height: 36px;
  border: 1px solid #E2E8F0;
  border-radius: 999px;
  background: #FFFFFF;
  color: #64748B;
  cursor: pointer;
  font-weight: 800;
  padding: 0 13px;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.chip:hover,
.chip.active {
  border-color: #1E6FDC;
  background: #EFF6FF;
  color: #1E6FDC;
}

.closure-panel,
.result-panel {
  margin-top: 18px;
}

.variance-box {
  display: grid;
  align-content: center;
  min-height: 58px;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  background: #F8FAFC;
  padding: 0 16px;
}

.variance-box strong {
  margin-top: 4px;
  font-size: 22px;
}

.positive { color: #15803D; }
.negative { color: #DC2626; }

.result-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.result-grid div {
  border-radius: 16px;
  background: #F8FAFC;
  padding: 14px;
}

.result-grid strong {
  display: block;
  margin-top: 8px;
  color: #0D1B3D;
  font-size: 20px;
}

.inline-success,
.message {
  margin: 12px 0 0;
  font-size: 13px;
  font-weight: 800;
}

.inline-success,
.success-message {
  color: #15803D;
}

.error-message {
  color: #DC2626;
}

.message {
  border-radius: 14px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  padding: 12px 14px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(13, 27, 61, 0.45);
  padding: 20px;
}

.modal {
  width: min(440px, 100%);
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  background: #FFFFFF;
  box-shadow: 0 24px 72px rgba(15, 23, 42, 0.24);
  padding: 26px;
}

.modal-mark {
  color: #1E6FDC;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.modal h3 {
  margin: 8px 0 14px;
  color: #0D1B3D;
  font-size: 22px;
}

.modal p {
  color: #64748B;
  margin-bottom: 8px;
}

.modal-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 22px;
}

@media (max-width: 980px) {
  .metrics-grid,
  .result-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .content-grid {
    grid-template-columns: minmax(0, 1fr);
    align-items: stretch;
  }
}

@media (max-width: 760px) {
  .page {
    padding: 22px 14px 88px;
  }

  .page-header,
  .panel-header,
  .expense-item {
    align-items: stretch;
    flex-direction: column;
  }

  h1 {
    font-size: 30px;
  }

  .metrics-grid,
  .content-grid,
  .form-row,
  .closure-grid,
  .result-grid,
  .modal-actions {
    grid-template-columns: 1fr;
  }

  .metric-card strong,
  .fund-input,
  .ca-input {
    font-size: 22px;
  }

  .panel {
    padding: 18px;
  }

  .expense-actions {
    justify-content: space-between;
    width: 100%;
  }
}

@media (max-width: 420px) {
  .page {
    padding-inline: 12px;
  }

  .metric-card,
  .panel,
  .modal {
    border-radius: 14px;
  }

  .modal {
    padding: 20px;
  }
}
</style>
